/**
 * RelationshipGatesBanner
 *
 * Renders compound relationship rule outcomes from itemRelationshipRuleEngine
 * as inline diagnostics. Used by Bloc commitment, Track entry, lineage
 * dissolution, and Collection scope activation surfaces.
 *
 * Per canon 07A §3, outcomes are grouped by gate priority so the user sees
 * the most critical blockers first.
 */

import { AlertTriangle, Ban, Info, ShieldAlert, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { RuleOutcome, RuleOutcomeLevel } from '../../lib/itemRelationshipRuleEngine';

// ============================================================================
// Per-level visual config
// ============================================================================

const LEVEL_CONFIG: Record<
  RuleOutcomeLevel,
  { icon: React.ElementType; bg: string; border: string; text: string; label: string }
> = {
  block: {
    icon: Ban,
    bg: 'bg-[var(--coral-dim)]',
    border: 'border-[var(--coral)]',
    text: 'text-[var(--coral)]',
    label: 'Blocked',
  },
  require_approval: {
    icon: ShieldAlert,
    bg: 'bg-[var(--mustard-dim)]',
    border: 'border-[var(--mustard)]',
    text: 'text-[var(--mustard)]',
    label: 'Requires approval',
  },
  warn: {
    icon: AlertTriangle,
    bg: 'bg-[var(--mustard-dim)]',
    border: 'border-[var(--mustard)]',
    text: 'text-[var(--mustard)]',
    label: 'Warning',
  },
  allow: {
    icon: Info,
    bg: 'bg-[var(--slate-dim)]',
    border: 'border-[var(--slate)]',
    text: 'text-[var(--text-sec)]',
    label: 'Notice',
  },
};

const GATE_LABELS: Record<1 | 2 | 3 | 4 | 5 | 6 | 7, string> = {
  1: 'Gate 1 — Cluster resolution',
  2: 'Gate 2 — Category declaration',
  3: 'Gate 3 — Scope completeness',
  4: 'Gate 4 — Selection integrity',
  5: 'Gate 5 — Track role',
  6: 'Gate 6 — Dependency',
  7: 'Gate 7 — Elaboration selection',
};

// ============================================================================
// Resolution link helpers
// ============================================================================

interface ResolutionAction {
  label: string;
  to: string;
}

/**
 * Returns a resolution action (link + label) for a given outcome, if one exists.
 * Uses the outcome's gate, reason code, and involvedRelationshipIds to build
 * the most specific link possible.
 */
function getResolutionAction(outcome: RuleOutcome, itemId?: string): ResolutionAction | null {
  const relIds = outcome.involvedRelationshipIds ?? [];

  switch (outcome.gate) {
    case 1: {
      if (outcome.reason === 'CLUSTER_REVIEW_REQUIRED') {
        const clusterId = relIds[0];
        return clusterId
          ? { label: 'Review cluster', to: `/prioritization/relationships/clusters/${clusterId}` }
          : { label: 'Review clusters', to: '/prioritization/relationships/clusters' };
      }
      if (outcome.reason === 'CLUSTER_BLOCKS_PACKAGE') {
        // relIds: [packageId, ...clusterIds]
        const packageId = relIds[0];
        return packageId
          ? { label: 'View package', to: `/prioritization/relationships/packages/${packageId}` }
          : { label: 'View packages', to: '/prioritization/relationships/packages' };
      }
      return { label: 'Review clusters', to: '/prioritization/relationships/clusters' };
    }
    case 2:
      // Frame declaration — scroll to Classification section on this item
      return itemId
        ? { label: 'Declare category', to: `/prioritization/items/${itemId}#item-section-classification` }
        : null;
    case 3:
      return { label: 'Manage collections', to: '/prioritization/collections' };
    case 4: {
      const packageId = relIds[0];
      return packageId
        ? { label: 'View package', to: `/prioritization/relationships/packages/${packageId}` }
        : { label: 'View packages', to: '/prioritization/relationships/packages' };
    }
    case 5:
      return { label: 'Manage Tracks', to: '/prioritization/tracks' };
    case 6:
    case 7:
      // Dependency / elaboration — edit edges on the item Relationships tab
      return itemId
        ? { label: 'View relationships', to: `/prioritization/items/${itemId}?tab=relationships` }
        : null;
    default:
      return null;
  }
}

// ============================================================================
// Component
// ============================================================================

interface RelationshipGatesBannerProps {
  outcomes: RuleOutcome[];
  /**
   * When true, only show block and require_approval outcomes.
   * Useful for compact surfaces where warns would be noise.
   */
  blocksOnly?: boolean;
  /**
   * The item ID this banner is being shown for. When provided, resolution
   * links that are item-specific (e.g. frame declaration, gates tab) will be
   * rendered. Pass this whenever the banner is shown in an item detail context.
   */
  itemId?: string;
  className?: string;
}

export function RelationshipGatesBanner({
  outcomes,
  blocksOnly = false,
  itemId,
  className = '',
}: RelationshipGatesBannerProps) {
  const visible = blocksOnly
    ? outcomes.filter((o) => o.level === 'block' || o.level === 'require_approval')
    : outcomes.filter((o) => o.level !== 'allow');

  if (visible.length === 0) return null;

  // Sort by gate ASC, then severity DESC within same gate
  const levelOrder: RuleOutcomeLevel[] = ['block', 'require_approval', 'warn', 'allow'];
  const sorted = [...visible].sort((a, b) => {
    if (a.gate !== b.gate) return a.gate - b.gate;
    return levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level);
  });

  return (
    <div className={`space-y-2 ${className}`}>
      {sorted.map((outcome, idx) => {
        const cfg = LEVEL_CONFIG[outcome.level];
        const Icon = cfg.icon;
        const action = getResolutionAction(outcome, itemId);
        return (
          <div
            key={idx}
            className={`flex items-start gap-2.5 p-3  border ${cfg.bg} ${cfg.border}`}
          >
            <Icon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${cfg.text}`} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <span className={`text-xs font-semibold uppercase tracking-wide ${cfg.text}`}>
                  {cfg.label}
                </span>
                <span className="text-xs text-[var(--text-ter)]">{GATE_LABELS[outcome.gate]}</span>
              </div>
              <p className={`text-sm ${cfg.text}`}>{outcome.message}</p>
              {action && (
                <Link
                  to={action.to}
                  className={`inline-flex items-center gap-1 mt-1.5 text-xs font-medium underline-offset-2 hover:underline ${cfg.text} opacity-80 hover:opacity-100`}
                >
                  {action.label}
                  <ArrowRight className="h-3 w-3" />
                </Link>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Inline summary badge (for list rows, card headers, etc.)
// ============================================================================

interface RelationshipGatesBadgeProps {
  outcomes: RuleOutcome[];
}

export function RelationshipGatesBadge({ outcomes }: RelationshipGatesBadgeProps) {
  const hasBlock = outcomes.some((o) => o.level === 'block');
  const hasApproval = outcomes.some((o) => o.level === 'require_approval');
  const hasWarn = outcomes.some((o) => o.level === 'warn');

  if (hasBlock) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--coral-dim)] text-[var(--coral)] border border-[var(--coral)]">
        <Ban className="h-3 w-3" />
        Blocked
      </span>
    );
  }
  if (hasApproval) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]">
        <ShieldAlert className="h-3 w-3" />
        Review required
      </span>
    );
  }
  if (hasWarn) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]">
        <AlertTriangle className="h-3 w-3" />
        Warning
      </span>
    );
  }
  return null;
}
